import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { canReadDeanConversations, deanComposerState, deanMembershipMessage, sendDeanConversation } from "@/lib/deanChatSession";
import { useDeanAccountAccess } from "@/hooks/use-dean-account-access";
import { DeanDealershipNotice } from "@/components/dean-dealership-notice";
import { intlLocale } from "@/lib/locale";
import { useToast } from "@/hooks/use-toast";
import {
  beginDeanExchange,
  clearDeanExchange,
  conversationHasAnswerForExchange,
  DEAN_RECONCILE_INTERVAL_MS,
  DEAN_RECONCILE_TIMEOUT_MS,
  DeanStreamInterruptedError,
  getDeanPendingExchange,
  getDeanRuntimeExchange,
  getDeanSessionRevision,
  hasDeanSession,
  isDeanNewChat,
  hasUnresolvedDeanExchange,
  readRememberedDeanConversation,
  rememberDeanConversation,
  shouldPollDeanConversation,
  streamDeanMessage,
  updateDeanExchange,
  updateDeanPendingExchange,
  useDeanLifecycleRevision,
} from "@/lib/deanChatLifecycle";
import { hasDeanMarkdownTable, renderDeanMarkdown } from "@/lib/deanMarkdown";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Trash2, Plus, Send, Bot, User, FileText,
  ChevronRight, Loader2, Paperclip, Upload, ThumbsUp, X, Copy, Check, AlertCircle,
} from "lucide-react";

type Conversation = {
  id: number;
  title: string;
  documentContext?: string | null;
  documentType?: string | null;
  createdAt: string;
  updatedAt?: string | null;
};

type Message = {
  id: number;
  exchangeId?: string | null;
  conversationId: number;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
  recordReference?: {
    kind: string;
    id: string;
    dealerId?: string;
  } | null;
};

type ConversationWithMessages = Conversation & { messages: Message[] };

const RECORD_REFERENCE_LABELS: Record<string, string> = {
  dealer_vendor: "saved vendor",
  vendor: "saved vendor",
  saved_tax_return: "saved tax return",
  tax: "saved tax return",
  inventory: "inventory",
  floorplan_lender: "floorplan lender",
  lender: "floorplan lender",
  bank_statement: "bank statement",
  bank_transaction: "bank transaction",
};

function getRecordReferenceLabel(reference: unknown): string | null {
  if (!reference || typeof reference !== "object" || Array.isArray(reference)) return null;
  const value = reference as Record<string, unknown>;
  if (typeof value.kind !== "string" || typeof value.id !== "string") return null;
  const label = RECORD_REFERENCE_LABELS[value.kind];
  const id = value.id.trim();
  if (!label || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(id)) return null;
  return `Source: ${label} · ID ${id}`;
}

const SUGGESTED_PROMPT_KEYS = [
  "suggestedPrompts.floorplanCost",
  "suggestedPrompts.prioritizeSelling",
  "suggestedPrompts.breakeven",
  "suggestedPrompts.negotiateRates",
  "suggestedPrompts.carryingCost",
  "suggestedPrompts.agingRisk",
];

function formatConvTimestamp(ts: string, group: string): string {
  const date = new Date(ts);
  if (group === "Today" || group === "Yesterday") {
    return date.toLocaleTimeString(intlLocale(), { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleDateString(intlLocale(), { month: "short", day: "numeric" });
}

function groupByRecency(conversations: Conversation[]): Record<string, Conversation[]> {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const groups: Record<string, Conversation[]> = {
    Today: [],
    Yesterday: [],
    "Last 7 Days": [],
    Older: [],
  };

  for (const conv of conversations) {
    const ts = conv.updatedAt || conv.createdAt;
    const date = new Date(ts);
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (dayStart >= today) {
      groups["Today"].push(conv);
    } else if (dayStart >= yesterday) {
      groups["Yesterday"].push(conv);
    } else if (dayStart >= sevenDaysAgo) {
      groups["Last 7 Days"].push(conv);
    } else {
      groups["Older"].push(conv);
    }
  }

  return groups;
}

function MessageBubble({
  message,
  onThumbsUp,
  thumbsUpSent,
}: {
  message: Message;
  onThumbsUp?: (messageId: number) => void;
  thumbsUpSent?: boolean;
}) {
  const { t } = useTranslation(["dean", "common"]);
  const [copied, setCopied] = useState(false);

  if (message.role === "system") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-1 px-2" data-testid={`system-message-${message.id}`}>
        <FileText className="w-3 h-3 shrink-0" />
        <span>{message.content}</span>
      </div>
    );
  }

  const isUser = message.role === "user";
  const recordReferenceLabel = !isUser ? getRecordReferenceLabel(message.recordReference) : null;

  function handleCopy() {
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={`flex w-full min-w-0 max-w-full gap-3 overflow-hidden ${isUser ? "flex-row-reverse" : "flex-row"}`} data-testid={`message-${message.id}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isUser ? "bg-primary" : "bg-primary/10"}`}>
        {isUser ? (
          <User className="w-3.5 h-3.5 text-primary-foreground" />
        ) : (
          <Bot className="w-3.5 h-3.5 text-primary" />
        )}
      </div>
      <div className={`flex flex-col gap-1 ${!isUser && hasDeanMarkdownTable(message.content) ? "w-0 flex-1 min-w-0 max-w-full" : "min-w-0 max-w-[80%]"}`}>
        <div className={`min-w-0 max-w-full overflow-hidden rounded-2xl px-4 py-2.5 text-sm ${isUser ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div
              className="prose-sm min-w-0 max-w-full break-words [overflow-wrap:anywhere] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderDeanMarkdown(message.content) }}
            />
          )}
        </div>
        {recordReferenceLabel && (
          <span
            className="self-start rounded-full bg-primary/5 px-2 py-0.5 text-[10px] text-muted-foreground"
            role="note"
            aria-label={recordReferenceLabel}
            data-testid={`message-source-${message.id}`}
          >
            {recordReferenceLabel}
          </span>
        )}
        {!isUser && (
          <div className="self-start flex items-center gap-1">
            <button
              className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-colors ${copied ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary hover:bg-primary/5"}`}
              onClick={handleCopy}
              data-testid={`button-copy-${message.id}`}
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? t("dean:message.copied") : t("dean:message.copy")}
            </button>
            {onThumbsUp && (
              <button
                className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full transition-colors ${thumbsUpSent ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-primary hover:bg-primary/5"}`}
                onClick={() => onThumbsUp(message.id)}
                disabled={thumbsUpSent}
                data-testid={`button-thumbs-up-${message.id}`}
              >
                <ThumbsUp className="w-3 h-3" />
                {thumbsUpSent ? t("dean:message.helpfulSaved") : t("dean:message.helpful")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StreamingBubble({ content, status }: { content: string; status?: string }) {
  return (
    <div className="flex w-full min-w-0 max-w-full gap-3 overflow-hidden">
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary/10">
        <Bot className="w-3.5 h-3.5 text-primary" />
      </div>
      <div className="min-w-0 max-w-[80%] overflow-hidden rounded-2xl px-4 py-2.5 text-sm bg-muted text-foreground">
        {content ? (
          <div
            className="prose-sm min-w-0 max-w-full break-words [overflow-wrap:anywhere] leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderDeanMarkdown(content) }}
          />
        ) : status ? (
          <p className="text-muted-foreground" role="status" aria-live="polite" data-testid="dean-progress-status">
            {status}
          </p>
        ) : (
          <div className="flex items-center gap-1.5 h-5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function DeanChatsPage() {
  const { missingDealership, canImpersonate, viewOnly, openDealerPicker } = useDeanAccountAccess();
  const { t } = useTranslation(["dean", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingBubbleRef = useRef<HTMLDivElement>(null);
  const pendingBubbleRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeConversationId, setActiveConversationIdState] = useState<number | null>(
    readRememberedDeanConversation,
  );
  const activeConversationRef = useRef(activeConversationId);
  const [sendError, setSendError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [knownDocType, setKnownDocType] = useState<string | null>(null);
  const [showExtractionPrompt, setShowExtractionPrompt] = useState(false);
  const [showRoutingPrompt, setShowRoutingPrompt] = useState(false);
  const [thumbsUpSent, setThumbsUpSent] = useState<Set<number>>(new Set());
  const [hasAutoSelectedMostRecent, setHasAutoSelectedMostRecent] = useState(isDeanNewChat);
  useDeanLifecycleRevision();

  const runtimeExchange = getDeanRuntimeExchange(activeConversationId);
  const pendingExchange = getDeanPendingExchange(activeConversationId);
  const isStreaming = runtimeExchange?.status === "streaming"
    || runtimeExchange?.status === "reconciling";
  const showRuntimeAnswer = Boolean(runtimeExchange && runtimeExchange.status !== "failed");
  const hasFailedExchange = runtimeExchange?.status === "failed"
    || (!runtimeExchange && pendingExchange?.status === "failed");
  const hasUnresolvedExchange = activeConversationId != null
    && hasUnresolvedDeanExchange(activeConversationId);
  const pendingUserMessage = runtimeExchange?.question ?? null;
  const streamingContent = runtimeExchange?.answer ?? "";
  const progressStatus = runtimeExchange?.progress;

  const setActiveConversationId = useCallback((conversationId: number | null) => {
    activeConversationRef.current = conversationId;
    setActiveConversationIdState(conversationId);
    rememberDeanConversation(conversationId);
  }, []);

  const SIDEBAR_MIN = 180;
  const SIDEBAR_MAX = 480;
  const SIDEBAR_DEFAULT = 256;
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("deanSidebarWidth");
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= SIDEBAR_MIN && parsed <= SIDEBAR_MAX) return parsed;
      }
    }
    return SIDEBAR_DEFAULT;
  });
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      if (dragCleanupRef.current) dragCleanupRef.current();
    };
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartWidthRef.current = sidebarWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = ev.clientX - dragStartXRef.current;
      const newWidth = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, dragStartWidthRef.current + delta));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = (ev: MouseEvent) => {
      isDraggingRef.current = false;
      dragCleanupRef.current = null;
      const delta = ev.clientX - dragStartXRef.current;
      const newWidth = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, dragStartWidthRef.current + delta));
      if (typeof window !== "undefined") {
        localStorage.setItem("deanSidebarWidth", String(newWidth));
      }
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    dragCleanupRef.current = () => {
      isDraggingRef.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [sidebarWidth]);

  const { data: conversations = [], isLoading: loadingList, error: conversationsError } = useQuery<Conversation[]>({
    queryKey: ["/api/conversations"],
    enabled: (query) => !sendError && canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error),
    retry: false,
    refetchInterval: (query) => canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error)
      ? 5 * 60 * 1000 : false,
    refetchOnMount: false,
  });

  const { data: activeConversation, isLoading: loadingConversation, error: conversationError } = useQuery<ConversationWithMessages>({
    queryKey: ["/api/conversations", activeConversationId],
    enabled: (query) => activeConversationId !== null && !sendError && !deanMembershipMessage(conversationsError)
      && canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error),
    retry: false,
    refetchInterval: (query) => canReadDeanConversations(hasDeanSession(), missingDealership, query.state.error)
      && !deanMembershipMessage(conversationsError) && shouldPollDeanConversation(
      query.state.data as ConversationWithMessages | undefined,
      getDeanPendingExchange(activeConversationId),
      getDeanRuntimeExchange(activeConversationId)?.status === "streaming",
    ) ? DEAN_RECONCILE_INTERVAL_MS : false,
  });

  const membershipMessage = deanMembershipMessage(conversationsError)
    ?? deanMembershipMessage(conversationError) ?? sendError;
  const needsDealership = missingDealership || !!membershipMessage;
  const discardConversation = useCallback((id: number) => {
    clearDeanExchange(id);
    qc.removeQueries({ queryKey: ["/api/conversations", id] });
    setActiveConversationId(null);
    setHasAutoSelectedMostRecent(true);
  }, [qc, setActiveConversationId]);

  useEffect(() => {
    if (activeConversationId !== null && (
      (conversationError as { status?: number } | null)?.status === 404 || needsDealership
    )) discardConversation(activeConversationId);
  }, [conversationError, needsDealership, activeConversationId, discardConversation]);

  useEffect(() => {
    if (!activeConversationId) return;
    const exchange = getDeanRuntimeExchange(activeConversationId);
    const pending = getDeanPendingExchange(activeConversationId);
    if (conversationHasAnswerForExchange(activeConversation, exchange ?? pending)) {
      clearDeanExchange(activeConversationId, exchange?.requestId);
    }
  }, [activeConversationId, activeConversation]);

  useEffect(() => {
    if (!activeConversationId || runtimeExchange?.status === "streaming") return;
    const exchange = runtimeExchange ?? pendingExchange;
    if (!exchange || exchange.status === "failed") return;
    const remaining = Math.max(0, exchange.startedAt + DEAN_RECONCILE_TIMEOUT_MS - Date.now());
    const timeout = window.setTimeout(() => {
      const latestRuntime = getDeanRuntimeExchange(activeConversationId);
      if (latestRuntime) {
        updateDeanExchange(activeConversationId, latestRuntime.requestId, { status: "failed" });
      } else {
        updateDeanPendingExchange(activeConversationId, "failed");
      }
    }, remaining);
    return () => window.clearTimeout(timeout);
  }, [
    activeConversationId,
    runtimeExchange?.requestId,
    runtimeExchange?.status,
    runtimeExchange?.startedAt,
    pendingExchange?.status,
    pendingExchange?.startedAt,
  ]);

  const deleteConversation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/conversations/${id}`);
      return id;
    },
    onSuccess: (deletedId) => {
      qc.invalidateQueries({ queryKey: ["/api/conversations"] });
      if (activeConversationId === deletedId) {
        setActiveConversationId(null);
      }
      toast({ title: t("dean:toast.conversationDeleted") });
    },
    onError: () => toast({ title: t("dean:toast.deleteFailed"), variant: "destructive" }),
  });

  // Scroll to the bottom when the conversation changes or when the user sends
  // a message (so their just-sent bubble is visible). While DEAN is streaming
  // we intentionally do NOT keep pinning to the bottom — instead, when the
  // answer starts, we scroll DEAN's bubble so it starts at the top of view.
  const prevScrolledConvRef = useRef<number | null>(null);
  useEffect(() => {
    if (loadingConversation) return;
    const convChanged = prevScrolledConvRef.current !== activeConversationId;
    if (convChanged) prevScrolledConvRef.current = activeConversationId;
    // Only scroll to bottom when opening a conversation or right after the
    // user sends a message — never when pendingUserMessage CLEARS (that
    // happens when a long answer finishes, and yanking to the bottom then
    // would pull the reader away from the top of DEAN's answer).
    if (convChanged || pendingUserMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeConversationId, pendingUserMessage, loadingConversation]);

  const hasScrolledToStreamRef = useRef(false);
  useEffect(() => {
    if (!showRuntimeAnswer) {
      hasScrolledToStreamRef.current = false;
      return;
    }
    if (streamingContent && !hasScrolledToStreamRef.current) {
      hasScrolledToStreamRef.current = true;
      const el = streamingBubbleRef.current;
      if (el) {
        // Keep up to ~3 lines of the user's question visible above the answer.
        const questionEl = pendingBubbleRef.current;
        const questionPeek = questionEl
          ? Math.min(questionEl.offsetHeight, 84) + 16
          : 8;
        el.style.scrollMarginTop = `${questionPeek}px`;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showRuntimeAnswer, streamingContent]);

  // Auto-select most recent conversation on initial load (pick up where user left off)
  useEffect(() => {
    if (needsDealership || hasAutoSelectedMostRecent) return;
    if (activeConversationId != null && conversations.some((conversation) => conversation.id === activeConversationId)) {
      setHasAutoSelectedMostRecent(true);
      return;
    }
    if (conversations.length > 0) {
      setActiveConversationId(conversations[0].id);
      setHasAutoSelectedMostRecent(true);
    }
  }, [needsDealership, conversations, hasAutoSelectedMostRecent, activeConversationId, setActiveConversationId]);

  const createConversationRecord = useCallback(async (): Promise<number> => {
    if (!hasDeanSession() || missingDealership || viewOnly) throw new DOMException("DEAN context unavailable", "AbortError");
    const revision = getDeanSessionRevision();
    const res = await apiRequest("POST", "/api/conversations", { title: "New Chat" });
    const conv = await res.json() as Conversation;
    if (revision !== getDeanSessionRevision()) throw new DOMException("Account changed", "AbortError");
    qc.setQueryData<ConversationWithMessages>(
      ["/api/conversations", conv.id],
      { ...conv, messages: [] },
    );
    qc.invalidateQueries({ queryKey: ["/api/conversations"] });
    setActiveConversationId(conv.id);
    return conv.id;
  }, [qc, setActiveConversationId, missingDealership, viewOnly]);

  const sendMessageToConversation = useCallback(async (conversationId: number, content: string) => {
    const revision = getDeanSessionRevision();
    if (!content.trim()) return;
    if (hasUnresolvedDeanExchange(conversationId)) return;
    const existing = getDeanRuntimeExchange(conversationId);
    const persisted = getDeanPendingExchange(conversationId);
    if (existing || persisted) clearDeanExchange(conversationId, existing?.requestId);

    setInput("");
    setActiveConversationId(conversationId);
    const exchange = beginDeanExchange(conversationId, content);
    const optimisticMessage: Message = {
      id: -exchange.requestId,
      conversationId,
      role: "user",
      content,
      createdAt: new Date(exchange.startedAt).toISOString(),
    };
    qc.setQueryData<ConversationWithMessages>(
      ["/api/conversations", conversationId],
      (current) => current
        ? { ...current, messages: [...current.messages, optimisticMessage] }
        : current,
    );

    try {
      await streamDeanMessage({
        conversationId,
        exchangeId: exchange.exchangeId,
        content,
        onAccepted: () => {
          if (revision !== getDeanSessionRevision()) return;
          void qc.invalidateQueries({ queryKey: ["/api/conversations", conversationId] });
          void qc.invalidateQueries({ queryKey: ["/api/conversations"] });
        },
        onContent: (answer) => {
          updateDeanExchange(conversationId, exchange.requestId, { answer });
        },
        onStatus: (progress) => {
          updateDeanExchange(conversationId, exchange.requestId, { progress });
        },
      });
      if (revision !== getDeanSessionRevision()) return;
      updateDeanExchange(conversationId, exchange.requestId, { status: "complete" });
      await qc.invalidateQueries({ queryKey: ["/api/conversations", conversationId] });
      void qc.invalidateQueries({ queryKey: ["/api/conversations"] });
    } catch (err: any) {
      if (revision !== getDeanSessionRevision()) return;
      if (err?.status === 404 || deanMembershipMessage(err)) {
        clearDeanExchange(conversationId, exchange.requestId);
        throw err;
      }
      const interrupted = err instanceof DeanStreamInterruptedError
        || err?.name === "TypeError";
      updateDeanExchange(
        conversationId,
        exchange.requestId,
        { status: interrupted ? "reconciling" : "failed" },
      );
      await qc.invalidateQueries({ queryKey: ["/api/conversations", conversationId] });
      void qc.invalidateQueries({ queryKey: ["/api/conversations"] });
      if (!interrupted) {
        toast({ title: t("dean:toast.sendFailed"), description: err.message, variant: "destructive" });
      }
    }
  }, [qc, setActiveConversationId, toast, t]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || !hasDeanSession() || isStreaming || isCreatingConversation || needsDealership || viewOnly) return;
    setIsCreatingConversation(true);
    try {
      await sendDeanConversation({
        activeId: () => activeConversationRef.current,
        create: createConversationRecord,
        send: (id) => sendMessageToConversation(id, content),
        discard: discardConversation,
      });
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      const message = deanMembershipMessage(err);
      setSendError(message);
      toast({ title: t("dean:toast.sendFailed"), description: message ?? err.message, variant: "destructive" });
    } finally { setIsCreatingConversation(false); }
  }, [isStreaming, isCreatingConversation, needsDealership, viewOnly, createConversationRecord, sendMessageToConversation, discardConversation, toast, t]);

  const handleSend = () => {
    if (input.trim()) sendMessage(input.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleThumbsUp = useCallback(async (messageId: number) => {
    if (!activeConversationId || thumbsUpSent.has(messageId)) return;
    setThumbsUpSent((prev) => new Set(prev).add(messageId));
    try {
      await fetch(`/api/conversations/${activeConversationId}/messages/${messageId}/thumbs-up`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // non-fatal
    }
  }, [activeConversationId, thumbsUpSent]);

  const handleNewChat = () => {
    setActiveConversationId(null);
    setSendError(null);
    setHasAutoSelectedMostRecent(true);
    setInput("");
    setShowExtractionPrompt(false);
    setShowRoutingPrompt(false);
    setKnownDocType(null);
  };

  const handleDocumentUpload = async (file: File) => {
    let convId = activeConversationId;
    if (!convId) {
      try {
        convId = await createConversationRecord();
      } catch {
        toast({ title: t("dean:toast.startConversationFirst"), variant: "destructive" });
        return;
      }
    }

    setUploadingDoc(true);
    setShowExtractionPrompt(false);
    setShowRoutingPrompt(false);
    setKnownDocType(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`/api/conversations/${convId}/document`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Upload failed");
      }

      const result = await res.json();
      qc.invalidateQueries({ queryKey: ["/api/conversations", convId] });
      qc.invalidateQueries({ queryKey: ["/api/conversations"] });

      if (result.isKnownType) {
        setKnownDocType(result.documentType);
        setShowExtractionPrompt(true);
      }

      toast({ title: t("dean:toast.documentUploaded"), description: t("dean:toast.documentUploadedDesc") });
      setShowRoutingPrompt(true);
    } catch (err: any) {
      toast({ title: t("dean:toast.uploadFailed"), description: err.message, variant: "destructive" });
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleDocumentUpload(file);
    e.target.value = "";
  };

  const groups = groupByRecency(conversations);
  const groupOrder = ["Today", "Yesterday", "Last 7 Days", "Older"];
  const groupLabels: Record<string, string> = {
    "Today": t("dean:groups.today"),
    "Yesterday": t("dean:groups.yesterday"),
    "Last 7 Days": t("dean:groups.last7Days"),
    "Older": t("dean:groups.older"),
  };
  const messages = activeConversation?.messages ?? [];
  const hasMessages = messages.filter((m) => m.role !== "system").length > 0;
  const { busy: isPending, disabled: composerDisabled } = deanComposerState({
    session: hasDeanSession(), needsDealership, viewOnly,
    creating: isCreatingConversation, unresolved: hasUnresolvedExchange, uploading: uploadingDoc,
  });

  return (
    <div className="flex h-full overflow-hidden" data-testid="dean-chats-page">
      {/* Left Sidebar */}
      <div
        className="shrink-0 border-r flex flex-col bg-muted/30 overflow-hidden relative"
        style={{ width: sidebarWidth }}
        data-testid="dean-sidebar"
      >
        <div className="p-3 border-b shrink-0">
          <Button
            onClick={handleNewChat}
            className="w-full gap-2"
            size="sm"
            data-testid="button-new-chat"
          >
            <Plus className="w-4 h-4" />
            {t("dean:newChat")}
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {loadingList && (
              <div className="space-y-1 p-1">
                <Skeleton className="h-9 w-full rounded-lg" />
                <Skeleton className="h-9 w-full rounded-lg" />
                <Skeleton className="h-9 w-full rounded-lg" />
              </div>
            )}

            {!loadingList && conversations.length === 0 && (
              <div className="text-center text-xs text-muted-foreground py-8 px-2">
                {t("dean:sidebar.noConversations")}
                <br />{t("dean:sidebar.startNewChat")}
              </div>
            )}

            {!loadingList && conversations.length > 0 && (
              <div className="space-y-4">
                {groupOrder.map((group) => {
                  const convs = groups[group];
                  if (!convs || convs.length === 0) return null;
                  return (
                    <div key={group} data-testid={`sidebar-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1">
                        {groupLabels[group] ?? group}
                      </p>
                      <div className="space-y-0.5">
                        {convs.map((conv) => (
                          <div
                            key={conv.id}
                            className={`group flex items-start gap-2 rounded-lg px-2 py-2 cursor-pointer transition-colors text-sm ${
                              activeConversationId === conv.id
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-muted text-foreground"
                            }`}
                            onClick={() => setActiveConversationId(conv.id)}
                            data-testid={`conversation-item-${conv.id}`}
                          >
                            <span className="mt-0.5 shrink-0">
                              {conv.documentType && conv.documentType !== "other" ? (
                                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                              ) : (
                                <Sparkles className="w-3.5 h-3.5 text-muted-foreground" />
                              )}
                            </span>
                            <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                              <span className="line-clamp-2 text-xs leading-snug" data-testid={`conversation-title-${conv.id}`}>
                                {conv.title}
                              </span>
                              <span className="text-[10px] text-muted-foreground leading-none" data-testid={`conversation-timestamp-${conv.id}`}>
                                {formatConvTimestamp(conv.updatedAt || conv.createdAt, group)}
                              </span>
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 opacity-0 group-hover:opacity-100 shrink-0 text-muted-foreground hover:text-destructive mt-0.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteConversation.mutate(conv.id);
                              }}
                              disabled={deleteConversation.isPending}
                              data-testid={`button-delete-conversation-${conv.id}`}
                              title={t("dean:sidebar.deleteConversation")}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>
        {/* Drag handle */}
        <div
          className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 z-10 transition-colors"
          onMouseDown={handleDragStart}
          data-testid="sidebar-drag-handle"
        />
      </div>

      {/* Right panel — active conversation */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Chat header */}
        <div className="px-6 py-3 border-b shrink-0 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold leading-tight" data-testid="heading-dean-chats">
              {activeConversation ? activeConversation.title : "DEAN"}
            </h1>
            <p className="text-xs text-muted-foreground leading-tight">{t("dean:subtitle")}</p>
          </div>
          {activeConversation?.documentType && activeConversation.documentType !== "other" && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full" data-testid="document-badge">
              <FileText className="w-3 h-3" />
              {activeConversation.documentType === "floorplan_statement" ? t("dean:docBadge.floorplan") : t("dean:docBadge.receivable")}
            </div>
          )}
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-6 py-4" data-testid="dean-chat-area">
          {needsDealership && <DeanDealershipNotice canImpersonate={canImpersonate} onViewAsDealer={openDealerPicker} />}
          {/* New / empty state — no conversation selected */}
          {!needsDealership && !activeConversationId && !isCreatingConversation && (
            <div className="flex flex-col items-center gap-6 py-10 max-w-2xl mx-auto" data-testid="empty-state">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <div className="text-center">
                <h2 className="text-lg font-semibold mb-1">{t("dean:empty.title")}</h2>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t("dean:empty.description")}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
                {SUGGESTED_PROMPT_KEYS.map((promptKey) => {
                  const prompt = t(`dean:${promptKey}`);
                  return (
                    <button
                      key={promptKey}
                      onClick={() => sendMessage(prompt)}
                      className="text-left text-sm px-3 py-2.5 rounded-xl border hover:border-primary/40 hover:bg-primary/5 transition-colors flex items-center gap-2 group"
                      data-testid={`suggested-prompt-${promptKey.split(".").pop()}`}
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>{prompt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!needsDealership && isCreatingConversation && !activeConversationId && (
            <div className="flex flex-col items-center justify-center h-40 gap-3" data-testid="creating-conversation">
              <Loader2 className="w-6 h-6 animate-spin text-primary/60" />
              <p className="text-sm text-muted-foreground">{t("dean:creating")}</p>
            </div>
          )}

          {!needsDealership && activeConversationId && loadingConversation && (
            <div className="space-y-4 max-w-2xl mx-auto">
              <div className="flex gap-3">
                <Skeleton className="w-7 h-7 rounded-full shrink-0" />
                <Skeleton className="h-16 w-64 rounded-2xl" />
              </div>
              <div className="flex gap-3 flex-row-reverse">
                <Skeleton className="w-7 h-7 rounded-full shrink-0" />
                <Skeleton className="h-10 w-48 rounded-2xl" />
              </div>
            </div>
          )}

          {!needsDealership && activeConversationId && !loadingConversation && !hasMessages && !showRuntimeAnswer && !hasFailedExchange && (
            <div className="flex flex-col items-center gap-6 py-10 max-w-2xl mx-auto" data-testid="suggested-prompts">
              <div className="text-center">
                <p className="text-sm font-medium mb-1">{t("dean:empty.title")}</p>
                <p className="text-xs text-muted-foreground">{t("dean:empty.tryQuestions")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
                {SUGGESTED_PROMPT_KEYS.map((promptKey) => {
                  const prompt = t(`dean:${promptKey}`);
                  return (
                    <button
                      key={promptKey}
                      onClick={() => sendMessage(prompt)}
                      className="text-left text-sm px-3 py-2.5 rounded-xl border hover:border-primary/40 hover:bg-primary/5 transition-colors flex items-center gap-2 group"
                      data-testid={`suggested-prompt-${promptKey.split(".").pop()}`}
                    >
                      <ChevronRight className="w-3.5 h-3.5 text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <span>{prompt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {activeConversationId && !loadingConversation && (
            <div className="space-y-4 max-w-2xl mx-auto">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  onThumbsUp={msg.role === "assistant" ? handleThumbsUp : undefined}
                  thumbsUpSent={thumbsUpSent.has(msg.id)}
                />
              ))}
              {pendingUserMessage && !(messages.length > 0 && messages[messages.length - 1].role === "user" && messages[messages.length - 1].content === pendingUserMessage) && (
                <div ref={pendingBubbleRef} className="flex gap-3 flex-row-reverse" data-testid="pending-user-message">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary">
                    <User className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                  <div className="flex flex-col gap-1 max-w-[80%]">
                    <div className="rounded-2xl px-4 py-2.5 text-sm bg-primary text-primary-foreground">
                      <p className="whitespace-pre-wrap">{pendingUserMessage}</p>
                    </div>
                  </div>
                </div>
              )}
              {showRuntimeAnswer && (
                <div
                  ref={streamingBubbleRef}
                  style={{ scrollMarginTop: "8px" }}
                  data-testid="dean-streaming-answer"
                >
                  <StreamingBubble content={streamingContent || ""} status={progressStatus} />
                </div>
              )}
              {hasFailedExchange && !membershipMessage && (
                <div className="flex gap-3" data-testid="dean-answer-interrupted">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-destructive/10">
                    <AlertCircle className="w-3.5 h-3.5 text-destructive" />
                  </div>
                  <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm border border-destructive/25 bg-destructive/5 space-y-3">
                    <p>{t("dean:recovery.interrupted")}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (runtimeExchange?.question) setInput(runtimeExchange.question);
                        if (activeConversationId) {
                          clearDeanExchange(activeConversationId, runtimeExchange?.requestId);
                        }
                        textareaRef.current?.focus();
                      }}
                      data-testid="button-retry-dean-question"
                    >
                      {t(runtimeExchange ? "dean:recovery.tryAgain" : "dean:recovery.dismissAndRetry")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Smart routing prompt after document upload */}
              {showRoutingPrompt && !isStreaming && (
                <div className="flex gap-3" data-testid="routing-prompt-card">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary/10">
                    <Bot className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-muted text-foreground space-y-3">
                    <p>{t("dean:routing.questionFull")}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={async () => {
                          setShowRoutingPrompt(false);
                          setExtracting(true);
                          try {
                            const res = await fetch(`/api/conversations/${activeConversationId}/extract`, {
                              method: "POST",
                              credentials: "include",
                            });
                            if (!res.ok) {
                              const err = await res.json();
                              throw new Error(err.error || "Extraction failed");
                            }
                            const result = await res.json();
                            qc.invalidateQueries({ queryKey: ["/api/conversations", activeConversationId] });
                            const docLabel = result.documentType === "receivable_report" ? t("dean:docLabel.receivableReport") : t("dean:docLabel.statement");
                            toast({
                              title: t("dean:toast.savedToPlatform"),
                              description: t("dean:toast.savedToPlatformDesc", { doc: docLabel, lender: result.lenderName ? ` (${result.lenderName})` : "" }),
                            });
                            setShowExtractionPrompt(false);
                          } catch (err: any) {
                            toast({ title: t("dean:toast.placeFailed"), description: err.message, variant: "destructive" });
                          } finally {
                            setExtracting(false);
                          }
                        }}
                        disabled={extracting}
                        data-testid="button-routing-yes"
                      >
                        {extracting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        {t("dean:routing.yes")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setShowRoutingPrompt(false); setShowExtractionPrompt(false); }}
                        data-testid="button-routing-no"
                      >
                        {t("dean:routing.no")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Extraction prompt banner */}
        {showExtractionPrompt && !showRoutingPrompt && knownDocType && activeConversationId && (
          <div className="mx-6 mb-2 p-3 rounded-xl border border-primary/30 bg-primary/5 flex items-center gap-3 shrink-0" data-testid="extraction-offer-banner">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Upload className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {knownDocType === "floorplan_statement" ? t("dean:extraction.floorplanTitle") : t("dean:extraction.receivableTitle")}
              </p>
              <p className="text-xs text-muted-foreground">
                {knownDocType === "floorplan_statement"
                  ? t("dean:extraction.floorplanDesc")
                  : t("dean:extraction.receivableDesc")}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => setShowExtractionPrompt(false)} data-testid="button-dismiss-extraction">
                {t("dean:extraction.dismiss")}
              </Button>
              <Button
                size="sm"
                disabled={extracting}
                onClick={async () => {
                  setExtracting(true);
                  try {
                    const res = await fetch(`/api/conversations/${activeConversationId}/extract`, {
                      method: "POST",
                      credentials: "include",
                    });
                    if (!res.ok) {
                      const err = await res.json();
                      throw new Error(err.error || "Extraction failed");
                    }
                    const result = await res.json();
                    qc.invalidateQueries({ queryKey: ["/api/conversations", activeConversationId] });
                    const docLabel = result.documentType === "receivable_report" ? t("dean:docLabel.receivableReport") : t("dean:docLabel.statement");
                    toast({
                      title: t("dean:toast.extractedSaved"),
                      description: t("dean:toast.extractedSavedDesc", { doc: docLabel, lender: result.lenderName ? ` (${result.lenderName})` : "" }),
                    });
                    setShowExtractionPrompt(false);
                  } catch (err: any) {
                    toast({ title: t("dean:toast.extractionFailed"), description: err.message, variant: "destructive" });
                  } finally {
                    setExtracting(false);
                  }
                }}
                data-testid="button-run-extraction"
              >
                {extracting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {t("dean:extraction.extractSave")}
              </Button>
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="px-6 pb-4 pt-2 shrink-0 border-t">
          <div className="flex gap-2 items-end rounded-xl border bg-background px-3 py-2 focus-within:ring-1 focus-within:ring-primary/30 transition-all max-w-2xl mx-auto">
            <input
              type="file"
              accept=".pdf"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              data-testid="input-file-upload"
            />
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => fileInputRef.current?.click()}
              disabled={composerDisabled}
              data-testid="button-attach-document"
              title={t("dean:input.attachTitle")}
            >
              {uploadingDoc ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Paperclip className="w-4 h-4" />
              )}
            </Button>
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(needsDealership ? "dean:dealershipRequired.inputPlaceholder" : "dean:input.placeholder")}
              className="flex-1 min-h-[36px] max-h-[120px] resize-none border-0 shadow-none focus-visible:ring-0 p-0 text-sm bg-transparent disabled:bg-muted disabled:opacity-60"
              disabled={composerDisabled}
              data-testid="input-message"
              rows={1}
            />
            <Button
              size="icon"
              className="shrink-0 h-8 w-8"
              onClick={handleSend}
              disabled={!input.trim() || composerDisabled}
              data-testid="button-send-message"
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" data-testid="dean-send-spinner" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-1.5">
            {t("dean:input.attachHint")}
          </p>
        </div>
      </div>
    </div>
  );
}
